import { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const AutocompleteInput = props => {
  const {
    label,
    variant,
    size,
    inputSx,
    darkMode,
    helperText,
    error,
    contextId,
    multiple,
    groupBy
  } = props;

  // track unmount to avoid setting state in getInputOptions
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    }
  }, []);

  const [state, setState] = useState({
    inputOptions: [],
    selectedOption: null,
    selectedOptions: []
  });

  const inputRef = useRef();

  // track value changes w/ ref to control onChange callback
  const valueRef = useRef();

  const getInputOptionsRef = useRef(props.getInputOptions);
  useEffect(() => {
    getInputOptionsRef.current = props.getInputOptions;
  }, [props.getInputOptions]);

  // the contextId can be used to isolate options to a context.
  // ex: show and select customers for a job ID
  useEffect(() => {
    // nothing to do if no contextId or selectedOption,
    // or if it's already selected
    if (!contextId) {
      return;
    } else {
      if (multiple) {
        if (!state.selectedOptions.length || !state.selectedOptions.find(opt => opt.contextId !== contextId)) {
          return;
        }
      } else {
        if (!state.selectedOption || state.selectedOption.contextId === contextId) {
          return;
        }
      }
    }

    setState(state => ({
      ...state,
      inputOptions: [],
      selectedOption: null,
      selectedOptions: []
    }));

    onChange(null, multiple ? [] : null);
  }, [contextId]);

  const getInputOptions = debounce(async (filterValue, id = null) => {
    let page = 1;
    let perPage = 100;

    let inputOptions = await getInputOptionsRef.current(page, perPage, filterValue, id);

    // because this is an async function, the component may have been unmounted.
    // check mountedRef before setting state to avoid a memory leak
    if (inputOptions && mountedRef.current) {
      setState(state => ({
        ...state,
        inputOptions
      }));
    }

    return inputOptions || state.inputOptions;
  }, 1000, { leading: true });

  const getOptionLabel = option => {
    return option && (option.optionLabel || option.labelStr || option.label);
  }

  const loadValue = async () => {
    let {
      inputOptions,
      //selectedOption,
      initialValue,
      value
    } = props;

    // collapse all value props into single one to work with
    value ??= initialValue;// ?? selectedOption?.value; // selectedOption prop isn't being used

    // if inputOptions hasn't been passed then get it from getInputOptions.
    // the value is passed here so that the options filter to show it.
    // if value is null then it'll show the first page until something is typed
    inputOptions ||= await getInputOptions(null, value, true);

    // get selectedOption from value, or selectedOptions if multiple
    let { selectedOption, selectedOptions } = state;
    if (multiple) {
      selectedOptions = inputOptions?.filter(opt => value.indexOf(opt.value) !== -1) || [];
    } else {
      selectedOption = inputOptions?.find(opt => opt.value == value);
    }

    setState(state => ({
      ...state,
      inputOptions,
      selectedOption,
      selectedOptions
    }));

    // call onChange if value has changed,
    // but call the prop directly to avoid updating the state again
    if (props.onChange) {
      if (multiple) {
        if (JSON.stringify(valueRef.current) !== JSON.stringify(value)) {
          props.onChange(null, selectedOptions);
        }
      } else {
        if (valueRef.current !== value) {
          props.onChange(null, selectedOption);
        }
      }
    }

    valueRef.current = value;
  }

  // update value from props
  useEffect(loadValue, [props.value]);

  const setupInput = () => {
    // load value, initialValue or selectedOption if selectedOption isn't set.
    // value and initialValue will cause selectedOption to be searched for
    // in inputOptions, which will be loaded by getInputOptions if needed.
    // alternatively passing selectedOption in the props will just load it,
    // which would be better when dealing with many inputs to avoid n+1
    let notLoaded = !state.selectedOption && state.inputOptions?.length === 0;
    if (notLoaded) {
      loadValue();
    }

    // setup oninput listener.
    // there might be a better way to do this with MUI
    if (inputRef.current) {
      let input = inputRef.current.querySelector('input');
      input.oninput = event => getInputOptions(event.target.value);
      input.onclick = () => getInputOptions();
    }
  }
  useEffect(setupInput, []);

  const onChange = (event, selectedOption) => {
    if (multiple) {
      // selectedOption is an array in this case
      setState(state => ({ ...state, selectedOptions: selectedOption }));
    } else {
      setState(state => ({ ...state, selectedOption }));
    }

    if (props.onChange) {
      props.onChange(event, selectedOption);
    }
  }

  const sx = props.sx || {};
  if (!sx.height) {
    sx.height = '100%';
  }

  const inputProps = {
    label,
    sx: {
      height: '100%',
      borderRadius: '2px !important'
    },
    variant: variant || 'outlined',
    size,
    ref: inputRef,
    helperText,
    error
  }

  if (inputSx) {
    for (let k in inputSx) {
      inputProps.sx[k] = inputSx[k];
    }
  }

  // styling handled by App.scss now
  //if (!darkMode) {
  //  inputProps.sx.backgroundColor = '#fff !important';
  //}

  // clear the options when the menu closes
  const onClose = () => setState(state => ({ ...state, inputOptions: [] }));

  const isOptionEqualToValue = (option, value) => {
    if (props.isOptionEqualToValue) {
      return props.isOptionEqualToValue(option, value);
    } else {
      return option.value === value.value;
    }
  }

  // the value must be defined because this is a controlled input.
  // if it's a multiple select then it must default to an empty array,
  // otherwise null or an empty string
  let value = multiple ? state.selectedOptions : state.selectedOption;
  value ||= multiple ? [] : null;

  return (
    <Autocomplete
      //inputValue={this.getOptionLabel(selectedOption)}
      value={value}
      options={state.inputOptions}
      sx={sx}
      renderInput={props => <TextField {...props} {...inputProps} />}
      onChange={onChange}
      getOptionLabel={getOptionLabel}
      onClose={onClose}
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={multiple}
      groupBy={groupBy}
    />
  );
}

const mapStateToProps = state => ({
  darkMode: state.portalReducer.darkMode
});

export default connect(mapStateToProps)(AutocompleteInput);
